import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import './theme/tokens.css';
import './theme/app.css';
import { createQueryClient } from './app/queryClient';
import { createOpsRouter } from './app/router';
import { SessionProvider } from './app/session';
import { ToastProvider } from './components';
import { AuthProvider } from './features/auth';

async function startMocks(): Promise<void> {
  if (import.meta.env.VITE_MOCK !== '1') return;
  const { worker } = await import('./mocks/browser');
  await worker.start({ onUnhandledRequest: 'bypass' });
}

function registerServiceWorker(): void {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js');
  });
}

async function bootstrap(): Promise<void> {
  await startMocks();

  const container = document.getElementById('app-root');
  if (!container) throw new Error('#app-root is missing from index.html');

  const queryClient = createQueryClient();
  const router = createOpsRouter();

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <AuthProvider>
            <ToastProvider>
              <RouterProvider router={router} />
            </ToastProvider>
          </AuthProvider>
        </SessionProvider>
      </QueryClientProvider>
    </StrictMode>,
  );

  registerServiceWorker();
}

void bootstrap();
